import React from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { toast } from 'react-toastify';
import { container } from 'tsyringe';
import { GetLocalStorage } from '../../../../misc/UseLocalStorage';
import { DataKepesantrenanPresenter } from '../../../../../data/presenters/DataKepesantrenanPresenter';
import { DataKepesantrenan } from '../../../../../entities/DataKepesantrenan';
import { usePelatihanSayaContext } from '../../../controller/registered/pelatihan_saya';

interface IState {
  isLoadingGet: boolean;
  isLoadingDownload: boolean;
  linkPDF: string;
  msgError: string;
  showModal: boolean;
  showKepesantrenanModal: boolean;
  kepesantrenanData: DataKepesantrenan | null;
  kepesantrenanLoading: boolean;
  pendingPelatihanId: number | null;
  pendingPelatihanNama: string;
  pendingAction: 'sertifikat' | 'daftar';
  pendingDaftarWaktoBuka: string;
  pendingDaftarWaktuTutup: string;
}

interface InitialState {
  state: IState;
  getSertifikat: (pelatihan_id: number) => void;
  downloadSertifikat: (pelatihan_id: number, nama_pelatihan: string) => void;
  setShowModal: (value: boolean) => void;
  openSertifikat: (pelatihanId: number, isForPesantren: number, pelatihanNama: string) => void;
  openDaftarWithKepesantrenan: (
    pelatihanId: number,
    isForPesantren: number,
    waktoBuka: string,
    waktuTutup: string,
  ) => void;
  submitKepesantrenanAndProceed: (
    pelatihanId: number,
    isPesantren: number,
    namaPesantren: string | null,
    nsp: string | null,
    alamat: string | null,
    provinsiId: number | null,
    kabupatenId: number | null,
    kecamatanId: number | null,
    peran: string | null,
    tahunBergabung: number | null,
    statusMukim: number | null,
  ) => void;
  closeKepesantrenanModal: () => void;
}

const initialState = {
  state: {
    isLoadingGet: false,
    isLoadingDownload: false,
    linkPDF: '',
    msgError: '',
    showModal: false,
    showKepesantrenanModal: false,
    kepesantrenanData: null,
    kepesantrenanLoading: false,
    pendingPelatihanId: null,
    pendingPelatihanNama: '',
    pendingAction: 'sertifikat' as 'sertifikat' | 'daftar',
    pendingDaftarWaktoBuka: '',
    pendingDaftarWaktuTutup: '',
  },
  getSertifikat: () => {},
  downloadSertifikat: () => {},
  setShowModal: () => {},
  openSertifikat: () => {},
  openDaftarWithKepesantrenan: () => {},
  submitKepesantrenanAndProceed: () => {},
  closeKepesantrenanModal: () => {},
};
const Context = React.createContext<InitialState>(initialState);
const { Provider: Sertifikat } = Context;

const Provider: React.FC = ({ children }) => {
  const { storePelatihanSaya } = usePelatihanSayaContext();
  const [state, setState] = React.useState<IState>({
    isLoadingGet: false,
    isLoadingDownload: false,
    linkPDF: '',
    msgError: '',
    showModal: false,
    showKepesantrenanModal: false,
    kepesantrenanData: null,
    kepesantrenanLoading: false,
    pendingPelatihanId: null,
    pendingPelatihanNama: '',
    pendingAction: 'sertifikat',
    pendingDaftarWaktoBuka: '',
    pendingDaftarWaktuTutup: '',
  });

  const token = GetLocalStorage({ name: 'tokenAuth' });

  const setLoading = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoadingGet: value,
    }));
  };

  const setLoadingDownload = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoadingDownload: value,
    }));
  };

  const setShowModal = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      showModal: value,
    }));
  };

  const closeKepesantrenanModal = () => {
    setState((prevstate) => ({
      ...prevstate,
      showKepesantrenanModal: false,
      pendingPelatihanId: null,
      pendingPelatihanNama: '',
    }));
  };

  const getSertifikat = (pelatihan_id: number) => {
    try {
      setLoading(true);
      axios.defaults.headers.common = {
        Authorization: token !== undefined && token,
      };

      // Ganti ke dev apabila ingin mencoba di local
      axios(`${process.env.REACT_APP_BE_PROD_BASE_URL}sertifikat/${pelatihan_id}`, {
        method: 'GET',
        responseType: 'blob',
      })
        .then((resp) => {
          const file = new Blob([resp.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          setState((prevstate) => ({
            ...prevstate,
            linkPDF: fileURL,
          }));
        })
        .catch(async (error) => {
          const status = error.response?.status;

          let beMessage = '';
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              beMessage = parsed.message || '';
            } catch {
              beMessage = '';
            }
          } else {
            beMessage = error.response?.data?.message || '';
          }

          console.error(`[getSertifikat] Error ${status || 'N/A'}:`, beMessage || error.message);

          let errorMessage = 'Terjadi kesalahan. Silakan coba lagi.';

          if (!error.response) {
            errorMessage = 'Koneksi bermasalah. Silakan cek koneksi internet dan coba lagi.';
          } else if (status === 401) {
            errorMessage = 'Sesi Anda telah berakhir. Silakan login kembali.';
          } else if (status && status >= 500) {
            errorMessage = 'Terdapat error pada sistem. Silahkan coba beberapa saat lagi, atau hubungi admin!';
          } else if (status === 400) {
            errorMessage = beMessage || 'Sertifikat belum dapat ditampilkan. Silakan coba beberapa saat lagi.';
          }

          toast.error(errorMessage, {
            style: {
              boxShadow: '0px 1px 6px #F86E70',
            },
          });
          setShowModal(false);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error: any) {
      console.error('error get sertifikat:', error);
      setLoading(false);
    }
  };

  const downloadSertifikat = (pelatihan_id: number, nama_pelatihan: string) => {
    try {
      setLoadingDownload(true);
      axios.defaults.headers.common = {
        Authorization: token !== undefined && token,
      };

      // Ganti ke dev apabila ingin mencoba di local
      axios(`${process.env.REACT_APP_BE_PROD_BASE_URL}sertifikat/${pelatihan_id}`, {
        method: 'GET',
        responseType: 'blob',
      })
        .then((resp) => {
          const file = new Blob([resp.data], { type: 'application/pdf' });
          saveAs(
            file,
            `Sertifikat ${nama_pelatihan} ${moment().format('DD-MM-YYYY')} .pdf`
          );
        })
        .catch(async (error) => {
          const status = error.response?.status;

          let beMessage = '';
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const parsed = JSON.parse(text);
              beMessage = parsed.message || '';
            } catch {
              beMessage = '';
            }
          } else {
            beMessage = error.response?.data?.message || '';
          }

          console.error(`[downloadSertifikat] Error ${status || 'N/A'}:`, beMessage || error.message);

          let errorMessage = 'Terjadi kesalahan. Silakan coba lagi.';

          if (!error.response) {
            errorMessage = 'Koneksi bermasalah. Silakan cek koneksi internet dan coba lagi.';
          } else if (status === 401) {
            errorMessage = 'Sesi Anda telah berakhir. Silakan login kembali.';
          } else if (status && status >= 500) {
            errorMessage = 'Terdapat error pada sistem. Silahkan coba beberapa saat lagi, atau hubungi admin!';
          } else if (status === 400) {
            errorMessage = beMessage || 'Sertifikat belum dapat ditampilkan. Silakan coba beberapa saat lagi.';
          }

          toast.error(errorMessage, {
            style: {
              boxShadow: '0px 1px 6px #F86E70',
            },
          });
        })
        .finally(() => {
          setLoadingDownload(false);
        });
    } catch (error: any) {
      console.error('error download sertifikat:', error);
      setLoadingDownload(false);
    }
  };

  const openSertifikat = async (
    pelatihanId: number,
    isForPesantren: number,
    pelatihanNama: string
  ) => {
    if (isForPesantren === 1) {
      setState((prevstate) => ({
        ...prevstate,
        kepesantrenanLoading: true,
      }));
      try {
        const presenter = container.resolve(DataKepesantrenanPresenter);
        const existingData = await presenter.getMyData();
        setState((prevstate) => ({
          ...prevstate,
          kepesantrenanData: existingData,
          kepesantrenanLoading: false,
          showKepesantrenanModal: true,
          pendingPelatihanId: pelatihanId,
          pendingPelatihanNama: pelatihanNama,
          pendingAction: 'sertifikat',
        }));
      } catch (error: any) {
        console.error('error fetch data kepesantrenan:', error);
        setState((prevstate) => ({
          ...prevstate,
          kepesantrenanLoading: false,
        }));
        toast.error('Gagal memuat data kepesantrenan. Silakan coba lagi.', {
          style: { boxShadow: '0px 1px 6px #F86E70' },
        });
      }
    } else {
      getSertifikat(pelatihanId);
      setShowModal(true);
    }
  };

  const openDaftarWithKepesantrenan = async (
    pelatihanId: number,
    isForPesantren: number,
    waktoBuka: string,
    waktuTutup: string,
  ) => {
    if (isForPesantren === 1) {
      setState((prevstate) => ({
        ...prevstate,
        kepesantrenanLoading: true,
      }));
      try {
        const presenter = container.resolve(DataKepesantrenanPresenter);
        const existingData = await presenter.getMyData();
        setState((prevstate) => ({
          ...prevstate,
          kepesantrenanData: existingData,
          kepesantrenanLoading: false,
          showKepesantrenanModal: true,
          pendingPelatihanId: pelatihanId,
          pendingPelatihanNama: '',
          pendingAction: 'daftar',
          pendingDaftarWaktoBuka: waktoBuka,
          pendingDaftarWaktuTutup: waktuTutup,
        }));
      } catch (error: any) {
        console.error('error fetch data kepesantrenan:', error);
        setState((prevstate) => ({
          ...prevstate,
          kepesantrenanLoading: false,
        }));
        toast.error('Gagal memuat data kepesantrenan. Silakan coba lagi.', {
          style: { boxShadow: '0px 1px 6px #F86E70' },
        });
      }
    } else {
      storePelatihanSaya(pelatihanId, waktoBuka, waktuTutup);
    }
  };

  const submitKepesantrenanAndProceed = async (
    pelatihanId: number,
    isPesantren: number,
    namaPesantren: string | null,
    nsp: string | null,
    alamat: string | null,
    provinsiId: number | null,
    kabupatenId: number | null,
    kecamatanId: number | null,
    peran: string | null,
    tahunBergabung: number | null,
    statusMukim: number | null,
  ) => {
    const { pendingAction, pendingDaftarWaktoBuka, pendingDaftarWaktuTutup } = state;
    try {
      const presenter = container.resolve(DataKepesantrenanPresenter);

      if (pendingAction === 'daftar') {
        await presenter.simpanMaster(
          isPesantren,
          namaPesantren,
          nsp,
          alamat,
          provinsiId,
          kabupatenId,
          kecamatanId,
          peran,
          tahunBergabung,
          statusMukim,
        );

        setState((prevstate) => ({
          ...prevstate,
          showKepesantrenanModal: false,
          pendingPelatihanId: null,
          pendingPelatihanNama: '',
          pendingAction: 'sertifikat',
          pendingDaftarWaktoBuka: '',
          pendingDaftarWaktuTutup: '',
        }));

        storePelatihanSaya(pelatihanId, pendingDaftarWaktoBuka, pendingDaftarWaktuTutup);
      } else {
        await presenter.simpan(
          pelatihanId,
          isPesantren,
          namaPesantren,
          nsp,
          alamat,
          provinsiId,
          kabupatenId,
          kecamatanId,
          peran,
          tahunBergabung,
          statusMukim,
        );

        setState((prevstate) => ({
          ...prevstate,
          showKepesantrenanModal: false,
          pendingPelatihanId: null,
          pendingPelatihanNama: '',
          pendingAction: 'sertifikat',
        }));

        getSertifikat(pelatihanId);
        setShowModal(true);
      }
    } catch (error: any) {
      console.error('error simpan data kepesantrenan:', error);
      toast.error('Gagal menyimpan data kepesantrenan. Silakan coba lagi.', {
        style: { boxShadow: '0px 1px 6px #F86E70' },
      });
    }
  };

  return (
    <Sertifikat
      value={{
        state,
        getSertifikat,
        downloadSertifikat,
        setShowModal,
        openSertifikat,
        openDaftarWithKepesantrenan,
        submitKepesantrenanAndProceed,
        closeKepesantrenanModal,
      }}
    >
      {children}
    </Sertifikat>
  );
};

export const useSertifikatContext = () => React.useContext(Context);
// eslint-disable-next-line
export default {
  useSertifikatContext,
  Provider,
};
